import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Phone, ChevronRight, CheckCircle2, ArrowLeft, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { BookInstallButton } from './holiday/BookInstallButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { serviceDetails } from '../blogData';
import { additionalServiceDetails } from '../newContent';
import { companyInfo, services } from '../mock';
import SEO from './SEO';
import { serviceSchema, breadcrumbSchema, faqPageSchema } from '../seo/schemas';

// Merge legacy + new service content. New entries override legacy keys
// when slugs overlap (e.g., ev-charger-installation).
const ALL_SERVICES = { ...serviceDetails, ...additionalServiceDetails };

// Preferred short URLs for the 6 primary services — used as canonicals so
// Google treats them as the authoritative URL even when visitors arrive via
// the legacy /services/:slug path.
const SHORT_URL_MAP = {
  'ev-charger-installation': '/ev-charger-installation-brooklyn',
  'electrical-panel-upgrades': '/panel-upgrade-brooklyn',
  'generator-installation': '/generator-installation-brooklyn',
  'smart-home-system-installation': '/smart-home-wiring-brooklyn',
  'emergency-repairs': '/emergency-electrician-brooklyn',
  'christmas-light-installation': '/christmas-light-installation-brooklyn',
};

const ServiceDetail = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const service = ALL_SERVICES[slug];
  const canonicalPath = SHORT_URL_MAP[slug] || `/services/${slug}`;

  if (!service) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <SEO title="Service Not Found" description="The service page you are looking for does not exist." path={`/services/${slug || ''}`} noindex />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Service Not Found</h1>
          <Link to="/">
            <Button className="bg-yellow-400 text-black font-bold">Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedServicesData = service.relatedServices
    .map(relSlug => ({ slug: relSlug, ...ALL_SERVICES[relSlug] }))
    .filter(s => s && s.title);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title={`${service.title} in Brooklyn, NY`}
        description={`${service.subtitle}. ${service.overview.slice(0, 110)} Experienced Brooklyn electricians serving Queens & Manhattan. Free estimates. Call (667) 455-6368.`}
        path={canonicalPath}
        image={service.image}
        keywords={`${service.title.toLowerCase()} brooklyn, ${service.title.toLowerCase()} nyc, ${service.title.toLowerCase()} cost, ${service.title.toLowerCase()} contractor`}
        jsonLd={[
          serviceSchema({
            name: service.title,
            description: service.overview,
            slug,
            image: service.image,
            faqs: service.faqs,
          }),
          faqPageSchema(service.faqs || []),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/#services' },
            { name: service.title, path: canonicalPath },
          ]),
        ]}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-yellow-400/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_jetco-electrical/artifacts/x1j92naa_Untitled%20design.png" 
                alt="Jetco Electrical" 
                className="h-12 w-auto"
              />
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold shadow-[0_0_15px_rgba(255,215,0,0.3)]">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-12 bg-black">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl"></div>
        </div>
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/')} className="flex items-center text-cyan-400 hover:text-yellow-400 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {service.title} <span className="text-yellow-400">in Brooklyn, NY</span>
              </h1>
              <p className="text-xl text-gray-400 mb-6">{service.subtitle}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold shadow-[0_0_20px_rgba(255,215,0,0.3)]"
                  onClick={scrollToContact}
                >
                  Get Free Estimate
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                {slug === 'christmas-light-installation' && <BookInstallButton />}
                <a href={`tel:${companyInfo.phone}`}>
                  <Button size="lg" variant="outline" className="w-full border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black">
                    <Phone className="w-5 h-5 mr-2" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(255,215,0,0.2)] border-2 border-yellow-400/30">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Overview</h2>
            <div className="w-24 h-1 bg-yellow-400 mx-auto mb-6"></div>
            <p className="text-lg text-gray-400">{service.overview}</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Key <span className="text-transparent bg-clip-text bg-yellow-400">Benefits</span></h2>
          <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-4">
            {service.benefits.map((benefit) => (
              <div key={benefit} className="flex items-start space-x-3 bg-gray-900 border border-gray-800 p-4 rounded-lg hover:border-yellow-400/50 transition-colors">
                <CheckCircle2 className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                <span className="text-gray-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Our <span className="text-transparent bg-clip-text bg-yellow-400">Process</span></h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {service.process.map((item) => (
                <div key={item.step} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-black font-bold text-xl shadow-[0_0_15px_rgba(255,215,0,0.3)]">
                      {item.step}
                    </div>
                  </div>
                  <div className="flex-1 pt-1">
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked <span className="text-transparent bg-clip-text bg-yellow-400">Questions</span></h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {service.faqs.map((faq) => (
                <AccordionItem key={faq.question} value={faq.question} className="bg-gray-900 border-2 border-gray-800 rounded-lg px-6 hover:border-yellow-400/50 transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-white hover:text-yellow-400">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-900 mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation and estimate
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-black text-white hover:bg-gray-900"
              onClick={scrollToContact}
            >
              Get Free Estimate
            </Button>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedServicesData.length > 0 && (
        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Related <span className="text-transparent bg-clip-text bg-yellow-400">Services</span></h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {relatedServicesData.map((relService) => (
                <Card key={relService.slug} className="hover:shadow-[0_0_30px_rgba(255,215,0,0.2)] transition-shadow border-2 border-gray-800 hover:border-yellow-400/50 bg-gray-900">
                  <CardHeader>
                    <CardTitle className="text-xl text-white">{relService.title}</CardTitle>
                    <CardDescription className="text-gray-400">{relService.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={SHORT_URL_MAP[relService.slug] || `/services/${relService.slug}`}>
                      <Button className="w-full bg-yellow-400 text-black font-bold hover:bg-yellow-500">
                        Learn More
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-black border-t border-yellow-400/30 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© 2025 {companyInfo.name}. All rights reserved.</p>
          <div className="mt-4">
            <a href={`tel:${companyInfo.phone}`} className="text-yellow-400 hover:text-cyan-400 transition-colors">{companyInfo.phone}</a>
            <span className="mx-3 text-gray-600">|</span>
            <a href={`mailto:${companyInfo.email}`} className="text-cyan-400 hover:text-yellow-400 transition-colors">{companyInfo.email}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDetail;
